import State from 'store/State';
import Order, {
    OrderColumn,
    OrderColumns,
    OrderMessageItemColumns,
} from 'communication/types/order/Order';
import {
    PortfolioItem,
    optionalPortfolioItemColumns,
} from 'portfolio/PortfolioItem';
import {
    selectAppSettings,
    useSelectAppSettings,
} from '../appSettings/selectors';
import { PortfolioItemColumn } from 'portfolio/PortfolioItem';
import { SymbologyValues } from '../appSettings/AppSettingsState';
import pick from 'lodash/pick';
import { OrderSide } from 'communication/types/order/OrderSide';

export function mapOrdersToPortfolioItems(
    state: State,
    orders: Order[]
): PortfolioItem[] {
    const { symbolField } = getMappedStreamFields(state);

    return orders.map((o) => mapOrderToPortfolioItem(o, symbolField));
}

interface MappedPortfolioItemValues {
    qtyTraded: number;
    qtyRemaining: number;
    previousNightClose: number;
    /*
     * This is the raw USD value from avgPxC before the currency has been applied
     */
    averagePrice: number;
    estCost: number;
    movement: number;
}

type MappedPortfolioColumns = keyof MappedPortfolioItemValues;

function orderColumnToPortfolioColumnMap(): Array<
    [OrderColumn, MappedPortfolioColumns]
> {
    return [
        ['fillQty', 'qtyTraded'],
        ['rem', 'qtyRemaining'],
        ['pncD', 'previousNightClose'],
        ['avgPxC', 'averagePrice'],
        ['movement', 'movement'],
        ['exCostTx', 'estCost'],
    ];
}

export function toOrderColumn(
    portfolioColumn: PortfolioItemColumn
): OrderColumn {
    const mappedFields = orderColumnToPortfolioColumnMap();

    const mapped = mappedFields.find((m) => m[1] === portfolioColumn);
    if (mapped) {
        return mapped[0];
    }

    return portfolioColumn as OrderColumn;
}

export function mapOrderToPortfolioItem(
    order: Order,
    symbolField: SymbolDataFields
): PortfolioItem {
    const mappedFields = orderColumnToPortfolioColumnMap();

    const mappedValues: MappedPortfolioItemValues =
        mappedFields.reduce<MappedPortfolioItemValues>(
            (accMappedValues, mapping) => {
                const orderColumn = mapping[0];
                const portfolioItemColumn = mapping[1];
                (accMappedValues[portfolioItemColumn] as unknown) =
                    (order[orderColumn] as number) ?? 0;
                return accMappedValues;
            },
            {} as MappedPortfolioItemValues
        );

    return {
        ...pick(order, optionalPortfolioItemColumns),
        symbol: order[symbolField] as string,
        instinetSymbol: order.isymbol,
        side: order.side,
        qty: order.qty ?? 0,
        ...mappedValues,
    };
}

export type SymbolDataFields =
    | 'bloom'
    | 'cusip'
    | 'localSymC'
    | 'sedol'
    | 'isymbol';

export interface MappedOrderStreamFields {
    symbolField: SymbolDataFields;
}

export function getMappedStreamFields(state: State): MappedOrderStreamFields {
    const { symbology } = selectAppSettings(state);
    const symbolField = mapSymbolStreamField(symbology);
    return { symbolField };
}

export function useMappedStreamFields() {
    const { symbology } = useSelectAppSettings();
    const symbolField = mapSymbolStreamField(symbology);
    return { symbolField };
}

export function mapToStreamField(
    state: State,
    column: PortfolioItemColumn
): OrderColumn {
    const { symbolField } = getMappedStreamFields(state);

    switch (column) {
        case 'symbol':
            return symbolField;
        case 'instinetSymbol':
            return 'isymbol';
        case 'qtyTraded':
            return 'fillQty';
        case 'qtyRemaining':
            return 'rem';
        case 'previousNightClose':
            return 'pncD';
        case 'averagePrice':
            return 'avgPxC';
        case 'movement':
            return 'movement';
        case 'estCost':
            return 'exCostTx';
    }

    return column;
}

export function mapSymbolStreamField(
    symbology: SymbologyValues
): SymbolDataFields {
    switch (symbology) {
        case SymbologyValues.Bloomberg:
            return 'bloom';
        case SymbologyValues.CUSIP:
            return 'cusip';
        case SymbologyValues.Local:
            return 'localSymC';
        case SymbologyValues.SEDOL:
            return 'sedol';
        default:
            return 'isymbol';
    }
}

export function mapOrderSide(
    orderSide: OrderSide,
    combineSellsAndShorts: boolean
): OrderSide {
    return orderSide === 'SS' && combineSellsAndShorts ? 'S' : orderSide;
}

export function mapToMessageColumns(
    clientOrderColumns: OrderColumns
): OrderMessageItemColumns {
    const ret: OrderMessageItemColumns = [];
    for (const col of clientOrderColumns) {
        switch (col) {
            case 'movement': {
                ret.push('pnc');
                ret.push('lp');
                break;
            }
            default:
                ret.push(col);
        }
    }
    return ret;
}
